import { CardContainer, Spinner } from '@insify/ui';
import { ContentContextWrapper, TrackedAnchor } from '@objectiv/tracker-react';
import clsx from 'clsx';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePolicies } from '../../api/queries/usePolicies';
import { GTMEvent, useTagManager } from '../../hooks/useTagManager';
import { useCountrySpecificContent } from '../../utils/localize';
import { hasAnyActiveCoverage, isActiveCoverage } from '../../utils/policies';
import { CoverageCard } from '../CoverageCard';
import { NoPolicies } from '../NoPolicies';
import { ProductCard } from '../Products';

import * as styles from './styles.module.css';

import type { Bundle, CompanyBundle, PersonalBundle } from '../../types/Policy';

export const PersonalInformationExcerpt: React.VFC = () => {
  const { sendEvent } = useTagManager();

  const { support_email } = useCountrySpecificContent();

  return (
    <p className={styles.personalInformationExcerpt}>
      <Trans
        i18nKey='policy:personal_information_excerpt'
        components={{
          a: (
            <TrackedAnchor
              objectiv={{ id: 'email' }}
              href={`mailto:${support_email}`}
              onClick={() => void sendEvent(GTMEvent.LET_US_KNOW)}
            />
          ),
        }}
      />
    </p>
  );
};

export const HomepagePolicyList: React.FC = () => {
  const { data: policies = [], isLoading } = usePolicies();

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner />
      </div>
    );
  }

  const activePolicies = policies.filter(hasAnyActiveCoverage);

  if (activePolicies.length === 0) {
    return <NoPolicies />;
  }

  return activePolicies.map((policy, i) => {
    return (
      <ContentContextWrapper key={`${policy.id}`} id={`policy-${i}`}>
        <PolicyCard policy={policy} />
      </ContentContextWrapper>
    );
  });
};

const PolicyCard = ({ policy }: { policy: Bundle }) => {
  const navigate = useNavigate();
  const onClick = () => {
    navigate(`/products/${policy.id}`);
  };

  return (
    <CardContainer className={styles.policyCard}>
      <div>
        <h2>{policy.type === 'company' ? (policy as CompanyBundle).companyName : (policy as PersonalBundle).name} </h2>
      </div>
      <div
        className={styles.innerCardContainer}
        onClick={onClick}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
        tabIndex={0}
        role='button'
      >
        <ProductCard
          key={`${policy.type}-${policy.country}`}
          className={clsx(styles.innerCard, styles.personalCard)}
          bundle={policy}
          additionalContent={policy.coverages.length > 0 && policy.coverages[0].policyNumber}
        />
      </div>
      {policy.coverages.filter(isActiveCoverage).map((coverage) => (
        <CoverageCard
          key={
            coverage.wordingsURL ?? (coverage.documents?.length ? coverage.documents[0].fileName : coverage.policyId)
          }
          className={styles.coverageCard}
          coverage={coverage}
          policy={policy}
        />
      ))}
    </CardContainer>
  );
};
